import { Controller } from "@hotwired/stimulus"
import { applyEnter, animateOut } from "./utils/motion"

// Dropdown menu (WAI-ARIA APG Menu Button pattern).
//
// Markup contract:
//   <div data-controller="dropdown">
//     <button data-action="dropdown#toggle" data-dropdown-target="trigger" aria-haspopup="menu">…</button>
//     <div data-dropdown-target="menu" role="menu" class="hidden">
//       <a role="menuitem" …>Item</a>
//     </div>
//   </div>
//
// A11y:
//   - aria-expanded sincronizado com estado aberto
//   - Esc fecha (com ou sem foco no menu)
//   - ↓ no trigger fechado abre + foca primeiro item
//   - ↑↓ navegam, Home/End vão pros extremos
//   - Click fora fecha
//   - Restaura foco ao trigger no close
export default class extends Controller {
  static targets = ["trigger", "menu"]

  connect() {
    this.boundOutside = (e) => {
      if (!this.element.contains(e.target)) this.close({ restoreFocus: false })
    }
    this.boundEsc = (e) => {
      if (e.key === "Escape" && this._isOpen()) this.close({ restoreFocus: true })
    }
    this.boundTriggerKeys = (e) => this._handleTriggerKeydown(e)
    this.boundMenuKeys    = (e) => this._handleMenuKeydown(e)
    // Só um dropdown aberto por vez: ao abrir, emitimos "dropdown:open"; os demais
    // escutam e fecham. Sem isso, abrir B não fechava A (o stopPropagation do
    // toggle impede o boundOutside dos outros de disparar).
    this.boundPeerOpen = (e) => {
      if (e.detail?.source !== this.element && this._isOpen()) this.close({ restoreFocus: false })
    }

    document.addEventListener("click", this.boundOutside)
    document.addEventListener("keydown", this.boundEsc)
    document.addEventListener("dropdown:open", this.boundPeerOpen)
    this.triggerTarget.addEventListener("keydown", this.boundTriggerKeys)
    this.menuTarget.addEventListener("keydown", this.boundMenuKeys)

    // Sincroniza aria-expanded com o estado inicial (menu nasce escondido).
    this.triggerTarget.setAttribute("aria-expanded", String(this._isOpen()))
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutside)
    document.removeEventListener("keydown", this.boundEsc)
    document.removeEventListener("dropdown:open", this.boundPeerOpen)
    this.triggerTarget.removeEventListener("keydown", this.boundTriggerKeys)
    this.menuTarget.removeEventListener("keydown", this.boundMenuKeys)
  }

  toggle(e) {
    e.stopPropagation()
    if (this._isOpen()) this.close({ restoreFocus: true })
    else this.open()
  }

  open() {
    // Avisa os outros dropdowns para fecharem (um aberto por vez).
    document.dispatchEvent(new CustomEvent("dropdown:open", { detail: { source: this.element } }))
    this.menuTarget.classList.remove("hidden")
    applyEnter(this.menuTarget, "animate-overlay-in")
    this.triggerTarget.setAttribute("aria-expanded", "true")
  }

  async close({ restoreFocus = false } = {}) {
    if (!this._isOpen()) return
    this.triggerTarget.setAttribute("aria-expanded", "false")
    // Restaura o foco já no início — esperar a animação atrasaria o foco e
    // prejudicaria usuários de teclado.
    if (restoreFocus) this.triggerTarget.focus()
    await animateOut(this.menuTarget, "animate-overlay-out")
    this.menuTarget.classList.add("hidden")
  }

  _isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }

  _items() {
    return Array.from(this.menuTarget.querySelectorAll(
      "[role='menuitem'], a, button:not([disabled])"
    )).filter((el) => el.offsetParent !== null)
  }

  _handleTriggerKeydown(e) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      this.open()
      requestAnimationFrame(() => {
        const items = this._items()
        if (items.length === 0) return
        const target = e.key === "ArrowUp" ? items[items.length - 1] : items[0]
        target.focus()
      })
    }
  }

  _handleMenuKeydown(e) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return
    e.preventDefault()
    const items = this._items()
    if (items.length === 0) return

    const current = items.indexOf(document.activeElement)
    let next = current

    switch (e.key) {
      case "ArrowDown": next = current < 0 ? 0 : (current + 1) % items.length; break
      case "ArrowUp":   next = current <= 0 ? items.length - 1 : current - 1; break
      case "Home":      next = 0; break
      case "End":       next = items.length - 1; break
    }
    items[next].focus()
  }
}
